// packages/charts/src/react/funnelExplorerModel.ts
//
// Pure derivation layer for <FunnelExplorerChart/> — turns the
// funnel_explorer hook payload into everything the view renders, so the
// arithmetic is unit-testable without a DOM.
//
// Edge-case contract (plan 2026-08-27-report-funnel-explorer-block,
// fixed BEFORE the tests so they can't codify an accident):
//   - A step with `missing: true` shows "—" and suppresses every rate
//     that reads through it (its own conversion, the previous step's
//     abandonment, per-day CTR into it).
//   - Zero base → rate is null ("—"), never 0%: no base is a gap, not
//     a measurement (same rule as the dashboard FunnelExplorer).
//   - A later step larger than an earlier one is NOT clamped — the
//     conversion may read above 100% — but abandonment shows "—" when
//     it would be negative (negative drop-off is not drop-off).
import type { FunnelExplorerData, FunnelExplorerStep } from "./types";

export interface FunnelStepModel {
  key: string;
  label: string;
  /** Absolute count for the period; null when the step is missing. */
  value: number | null;
  missing: boolean;
  /** Conversion from the PREVIOUS step, 0-100+; null for step 1, a zero
   * base, or a missing leg. Step 1 renders "100%" via `isFirst`. */
  conversionPct: number | null;
  /** Share of step 1 reaching this step (0-100+); null when unreadable. */
  completionPct: number | null;
  /** People lost between this step and the next. Null on the last step,
   * a missing leg, or when the difference is negative (non-sequential
   * metrics — negative drop-off is not drop-off). */
  abandonmentCount: number | null;
  abandonmentPct: number | null;
  /** Comparison-period value + delta vs it; null without comparison. */
  comparisonValue: number | null;
  deltaPct: number | null;
  /** Share of the COMPARISON period's step 1 reaching this step —
   * the counterpart of completionPct; null when unreadable. */
  comparisonCompletionPct: number | null;
  isFirst: boolean;
  isLast: boolean;
}

export interface FunnelDetailModel {
  key: string;
  label: string;
  nextLabel: string | null;
  dates: string[];
  counts: (number | null)[];
  /** Per-day conversion to the NEXT step (0-100+), null on a zero/absent
   * base day or when there is no next step. */
  stepRates: (number | null)[] | null;
  /** Comparison-window counts for the same step, aligned POSITIONALLY to
   * `dates` (day i of this window vs day i of the comparison window — the
   * day-shift semantics every period comparison uses; the windows'
   * calendar dates are disjoint by definition). Trimmed/null-padded to
   * `dates.length`. Null when the payload carries no comparison series. */
  comparisonCounts: (number | null)[] | null;
  /** The comparison window's own dates, index-aligned with
   * `comparisonCounts` where present — for tooltips. */
  comparisonDates: string[] | null;
  /** Comparison-window conversion to the next step, computed on the
   * comparison window's OWN day pairs, then aligned positionally like
   * `comparisonCounts`. Null without a comparison series or next step. */
  comparisonStepRates: (number | null)[] | null;
}

export interface FunnelBreakdownCellModel {
  value: number | null;
  comparisonValue: number | null;
  deltaPct: number | null;
}

export interface FunnelBreakdownRowModel {
  name: string;
  cells: FunnelBreakdownCellModel[];
}

export interface FunnelExplorerModel {
  steps: FunnelStepModel[];
  hasComparison: boolean;
  breakdownLabel: string | null;
  /** First row is always the Total row (values from `steps`, NOT the
   * column sums — the row set may be a capped top-N). */
  breakdownRows: FunnelBreakdownRowModel[];
  maxValue: number;
}

function pct(numerator: number, denominator: number): number | null {
  if (!Number.isFinite(denominator) || denominator <= 0) return null;
  return (numerator / denominator) * 100;
}

function deltaPct(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null) return null;
  if (!Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function stepValue(s: FunnelExplorerStep | undefined): number | null {
  if (!s || s.missing) return null;
  return Number.isFinite(s.value) ? s.value : null;
}

export function buildFunnelExplorerModel(
  data: FunnelExplorerData,
): FunnelExplorerModel {
  const steps = data.steps ?? [];
  const cmpSteps = data.comparison?.steps ?? null;
  const cmpByKey = new Map(
    (cmpSteps ?? []).map((s) => [s.key, s] as const),
  );
  const hasComparison = !!cmpSteps?.length;
  const firstValue = stepValue(steps[0]);
  const firstCmpValue = hasComparison
    ? stepValue(steps[0] ? cmpByKey.get(steps[0].key) : undefined)
    : null;

  const stepModels: FunnelStepModel[] = steps.map((s, i) => {
    const value = stepValue(s);
    const prevValue = i > 0 ? stepValue(steps[i - 1]) : null;
    const nextValue = i < steps.length - 1 ? stepValue(steps[i + 1]) : null;

    let abandonmentCount: number | null = null;
    let abandonmentPct: number | null = null;
    if (i < steps.length - 1 && value != null && nextValue != null) {
      const lost = value - nextValue;
      if (lost >= 0) {
        abandonmentCount = lost;
        abandonmentPct = pct(lost, value);
      }
    }

    const cmp = cmpByKey.get(s.key);
    const comparisonValue = hasComparison ? stepValue(cmp) : null;

    return {
      key: s.key,
      label: s.step,
      value,
      missing: !!s.missing || value == null,
      conversionPct:
        i === 0 || value == null || prevValue == null
          ? null
          : pct(value, prevValue),
      completionPct:
        value == null || firstValue == null ? null : pct(value, firstValue),
      abandonmentCount,
      abandonmentPct,
      comparisonValue,
      deltaPct: deltaPct(value, comparisonValue),
      comparisonCompletionPct:
        comparisonValue == null || firstCmpValue == null
          ? null
          : pct(comparisonValue, firstCmpValue),
      isFirst: i === 0,
      isLast: i === steps.length - 1,
    };
  });

  // Breakdown table — merge comparison rows by DIMENSION VALUE, never by
  // index (the two row sets are independently ranked top-Ns).
  const breakdown = data.breakdown ?? null;
  const cmpBreakdownByName = new Map(
    (data.comparison?.breakdown?.rows ?? []).map((r) => [r.name, r] as const),
  );
  const breakdownRows: FunnelBreakdownRowModel[] = [];
  if (breakdown) {
    breakdownRows.push({
      name: "Total",
      cells: stepModels.map((m) => ({
        value: m.value,
        comparisonValue: m.comparisonValue,
        deltaPct: m.deltaPct,
      })),
    });
    for (const row of breakdown.rows ?? []) {
      const cmpRow = cmpBreakdownByName.get(row.name);
      breakdownRows.push({
        name: row.name,
        cells: steps.map((_, i) => {
          const value = Number.isFinite(row.values?.[i])
            ? (row.values[i] as number)
            : null;
          const comparisonValue =
            hasComparison && cmpRow && Number.isFinite(cmpRow.values?.[i])
              ? (cmpRow.values[i] as number)
              : null;
          return { value, comparisonValue, deltaPct: deltaPct(value, comparisonValue) };
        }),
      });
    }
  }

  const maxValue = Math.max(
    1,
    ...stepModels.map((m) => m.value ?? 0),
    ...stepModels.map((m) => m.comparisonValue ?? 0),
  );

  return {
    steps: stepModels,
    hasComparison,
    breakdownLabel: breakdown?.label ?? null,
    breakdownRows,
    maxValue,
  };
}

/** Per-day series for one step — the right-hand detail panel. Dates come
 * keyed from the payload (the resolver already merged the per-step
 * queries onto one date union); an explicit null stays null (unknown ≠
 * zero) and renders as a gap. */
export function buildFunnelDetailModel(
  data: FunnelExplorerData,
  stepKey: string,
): FunnelDetailModel | null {
  const series = data.series;
  const steps = data.steps ?? [];
  const idx = steps.findIndex((s) => s.key === stepKey);
  if (!series || idx < 0) return null;
  const step = steps[idx];
  if (step.missing) return null;

  const counts = series.values?.[step.key];
  if (!Array.isArray(counts)) return null;

  const next = steps[idx + 1];
  const nextCounts =
    next && !next.missing ? series.values?.[next.key] : undefined;

  let stepRates: (number | null)[] | null = null;
  if (Array.isArray(nextCounts)) {
    stepRates = counts.map((cur, j) => {
      const nxt = nextCounts[j];
      if (cur == null || cur <= 0 || nxt == null) return null;
      return (nxt / cur) * 100;
    });
  }

  const dates = series.dates ?? [];

  // Comparison bars: positional day-shift alignment (day i vs day i),
  // trimmed/null-padded to the actual window's length so ECharts renders
  // the two bar series on one category axis.
  let comparisonCounts: (number | null)[] | null = null;
  let comparisonDates: string[] | null = null;
  let comparisonStepRates: (number | null)[] | null = null;
  const cmpSeries = data.comparison?.series;
  const cmpRaw = cmpSeries?.values?.[step.key];
  if (Array.isArray(cmpRaw)) {
    comparisonCounts = dates.map((_, j) => cmpRaw[j] ?? null);
    comparisonDates = dates.map((_, j) => cmpSeries?.dates?.[j] ?? "");
    // CTR on the comparison window's OWN day pairs (cmp step ÷ cmp next,
    // same day of that window), then positionally aligned like the counts.
    const cmpNext =
      next && !next.missing ? cmpSeries?.values?.[next.key] : undefined;
    if (Array.isArray(cmpNext)) {
      comparisonStepRates = dates.map((_, j) => {
        const cur = cmpRaw[j];
        const nxt = cmpNext[j];
        if (cur == null || cur <= 0 || nxt == null) return null;
        return (nxt / cur) * 100;
      });
    }
  }

  return {
    key: step.key,
    label: step.step,
    nextLabel: next && !next.missing ? next.step : null,
    dates,
    counts,
    stepRates,
    comparisonCounts,
    comparisonDates,
    comparisonStepRates,
  };
}

/** "—" for null; otherwise the meta-formatted value. Shared by the view
 * and its tests so "what does a missing number look like" has one home. */
export function formatOrDash(
  value: number | null | undefined,
  format: (v: number) => string,
): string {
  return value == null || !Number.isFinite(value) ? "—" : format(value);
}
